import { Link } from 'react-router';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import React from 'react';
import _ from 'lodash';
import classNames from 'classnames';
import moment from 'moment';

import { shortDate } from 'utils/format-utils';
import { CC_DOMAIN } from 'constants';
import { Card, FormCard } from 'components/content-containers/cards';
import { MetadataTable, MetadataItem } from 'components/content-containers/tables';
import { PageContent } from 'components/page';
import { PrimaryButton, SVGButton } from 'components/widgets/buttons/buttons';
import { RemoveButton } from 'components/widgets/buttons';
import { Timeline, TimelineItem } from 'components/widgets/timeline';
import AddTask from 'components/models/ticket/add-task';
import BillableIcon from 'components/models/agreement/billable-icon';
import CompanyLink from 'components/models/company/company-link';
import CopyToClipboard from 'components/widgets/icons/copy-to-clipboard';
import EditTicketForm from 'components/models/ticket/forms/edit-ticket-form';
import FlipMove from 'components/widgets/animation/flip-move';
import MergeTicketForm from 'components/models/ticket/forms/merge-ticket-form';
import Person from 'services/data/person';
import SVG from 'components/widgets/icons/svg';
import Task from 'services/data/task';
import TaskComponent from 'components/models/ticket/task';
import Ticket from 'services/data/ticket';
import Team from 'services/data/team';
import Tooltip from 'components/widgets/overlays/tooltip';
import Triage from 'services/data/triage';
import TriageItem from 'components/models/triage/triage-items';


// Component
class TicketSummary extends React.Component {
  static propTypes = {
    currentUser: PropTypes.object,
    updateTask: PropTypes.func,
    updateTicket: PropTypes.func,
    updateTriage: PropTypes.func,
    saveNewTask: PropTypes.func,
    ticket: PropTypes.object,
    teams: PropTypes.array
  };

  constructor() {
    super();

    this.state = { editing: false, merging: false, showAllTriageItems: false };
    this.removeTriage = this.removeTriage.bind(this);
    this.openEditForm = () => this.setState({ editing: true });
    this.openMergeForm = () => this.setState({ merging: true });
    this.closeForms = () => this.setState({ editing: false, merging: false });
  }

  removeTriage(item) {
    const { currentUser, updateTriage, triageItems } = this.props;
    const update = { triageId: item.triageId, claimedBy: currentUser.id, status: 'Active', ticket: null };

    updateTriage(_.reject(triageItems, { triageId: item.triageId }));

    return Triage.update(update);
  }

  render() {
    const { ticket, currentUser, teams, updateTask, triageItems: rawTriageItems, updateTicket, saveNewTask, updateTriage } = this.props;
    const { editing, merging, showAllTriageItems } = this.state;
    if (!ticket || _.isEmpty(ticket)) return null;

    const maxTriageItems = 20;
    const tasks = Task.sort(_.filter(ticket.tasks, { status: 'Active' }));
    const triageItems = _.orderBy(rawTriageItems, [item => moment(item.createdAt).toDate()], ['desc']);
    const limitTriageItems = triageItems.length > maxTriageItems && !showAllTriageItems;
    const ticketCC = `${Ticket.getTicketCC(ticket)},${Ticket.getTeamEmails(ticket, currentUser, teams)}`;
    const teamCC = `${Ticket.getTicketCC(ticket)},${Team.getTeamEmails(currentUser, teams)}`;
    const hasAgreement = _.get(ticket, 'agreement.id');
    const hasWorkOrder = _.get(ticket, 'workOrder.id');
    const isMerged = _.get(ticket, 'mergedTo.id');
    const isArchived = ticket.status === 'Archived';
    const isLead = Person.isLead(currentUser);
    const billable = ticket.timeDefaultBillable;
    const team = Ticket.getTeam(ticket, teams);
    const expectedCompletion = ticket.expectedCompletion;

    const formValues = _.assign({
      agreement: _.get(ticket, 'agreement.id'),
      workOrder: _.get(ticket, 'workOrder.id'),
      company: ticket.company.id
    }, _.pick(ticket, ['description', 'type', 'id', 'resolution', 'status', 'adminOnly', 'timeDefaultBillable', 'expectedCompletion']));

    return (
      <PageContent className="bg-medium-blue-grey ticket">
        <div className="ticket-summary">
          <div className="ticket-summary-cards">
            {editing &&
              <FormCard className="edit-ticket-form ticket-form">
                <EditTicketForm
                  initialValues={formValues}
                  close={this.closeForms}
                  ticket={ticket}
                  closeOnSubmit
                />
              </FormCard>
            }

            {merging &&
              <FormCard className="merge-ticket-form ticket-form">
                <MergeTicketForm
                  close={this.closeForms}
                  closeOnSubmit
                  ticketToMerge={ticket}
                  initialValues={{ company: _.get(ticket, 'company.id'), agreement: _.get(ticket, 'agreement.id') }}
                />
              </FormCard>
            }

            {!editing && !merging &&
              <Card className={classNames('ticket-details', _.kebabCase(ticket.type))}>
                <div className="card-header">{ticket.type}</div>
                {isLead &&
                  <Tooltip
                    title={ticket.adminOnly ? 'Only Praece Administrators can access this ticket' : 'All users can access this ticket'}
                    elementType="div"
                    className={classNames('ticket-security-header', { 'admin-only': ticket.adminOnly })}
                  >
                    <SVG src="svg/shield.svg" />
                    {ticket.adminOnly ? 'Praece Administrators Only' : 'Visible to All Users'}
                  </Tooltip>
                }
                <div className="ticket-summary-data">
                  <div className="card-icon-buttons">
                    <SVGButton src="svg/merge.svg" onClick={this.openMergeForm} className="merge-button link" />
                    <SVGButton src="svg/edit.svg" onClick={this.openEditForm} className="edit-button edit-link" />
                  </div>
                  <MetadataTable>
                    <MetadataItem label="Company">
                      <CompanyLink company={ticket.company} />
                    </MetadataItem>
                    {hasAgreement &&
                      <MetadataItem label="Agreement">
                        <Link to={`/admin/agreements/${ticket.agreement.id}/summary`}>{ticket.agreement.title}</Link>
                      </MetadataItem>
                    }
                    {hasWorkOrder &&
                      <MetadataItem label="Work Order">
                        {ticket.workOrder.number}: {ticket.workOrder.description}
                      </MetadataItem>
                    }
                    <MetadataItem label="Billable">
                      <div className="d-flex align-items-center">
                        <div style={{ lineHeight: 0 }}><BillableIcon billable={billable} /></div>
                        <span className="ml-2">Time is {billable ? 'Billable' : 'Non-Billable'} by Default</span>
                      </div>
                    </MetadataItem>
                    <MetadataItem label="PO Number">
                      <CopyToClipboard component="span" copyText={`${currentUser.poPrefix}-${ticket.id}`} tooltip="Copy the PO number for this ticket">
                        <SVG src="svg/clipboard.svg" className="clipboard" />
                        <span>{currentUser.poPrefix}-{ticket.id}</span>
                      </CopyToClipboard>
                    </MetadataItem>
                    {team &&
                      <MetadataItem label="Ticket Team Email">
                        <CopyToClipboard component="span" copyText={ticketCC} tooltip="Copy the ticket CC and ticket team members' emails">
                          <SVG src="svg/clipboard.svg" className="clipboard" />
                          <span>ticket-{ticket.id} & {team.name}</span>
                        </CopyToClipboard>
                      </MetadataItem>
                    }
                    <MetadataItem label="My Team Email">
                      <CopyToClipboard component="span" copyText={teamCC} tooltip="Copy the ticket CC and your team members' emails">
                        <SVG src="svg/clipboard.svg" className="clipboard" />
                        <span>ticket-{ticket.id} & {currentUser.team.name}</span>
                      </CopyToClipboard>
                    </MetadataItem>
                    <MetadataItem label="Ticket Email">
                      <CopyToClipboard component="span" copyText={Ticket.getTicketCC(ticket)} tooltip="Copy the ticket CC only">
                        <SVG src="svg/clipboard.svg" className="clipboard" />
                        <span>ticket-{ticket.id}@{CC_DOMAIN}</span>
                      </CopyToClipboard>
                    </MetadataItem>
                    <MetadataItem label="Search Gmail">
                      <a className="gmail-search-link" href={Ticket.getGmailSearchLink(ticket)} target="_blank"><SVG src="svg/email.svg" className="gmail" /> Gmail</a>
                    </MetadataItem>
                    <MetadataItem label={<span>Expected<br />Completion Date</span>}>
                      {expectedCompletion &&
                        <span>{shortDate(expectedCompletion)}</span>
                      }
                    </MetadataItem>
                  </MetadataTable>
                </div>
              </Card>
            }
            <Card className="ticket-timeline">
              <Timeline>
                {ticket.status === 'Active' &&
                  <TimelineItem>
                    {ticket.resolution && <h3>Ticket Reopened</h3>}
                    {!ticket.resolution && <h3>Ticket Currently Active</h3>}
                  </TimelineItem>
                }

                {ticket.resolution &&
                  <TimelineItem userId={_.get(ticket, 'closedBy.id')} date={ticket.closedAt}>
                    <h3>Ticket Closed</h3>
                    <p className="ticket-timeline-resolution">Resolution: {ticket.resolution}</p>
                  </TimelineItem>
                }

                {(isArchived && !isMerged) &&
                  <TimelineItem date={ticket.closedAt}>
                    <h3>Ticket Archived</h3>
                  </TimelineItem>
                }

                {(isArchived && isMerged) &&
                  <TimelineItem date={ticket.closedAt}>
                    <h3>Ticket Merged</h3>
                    <p className="ticket-timeline-resolution">
                      Merged into: <Link to={`/tickets/${ticket.mergedTo.id}`}>{ticket.mergedTo.description}</Link>
                    </p>
                  </TimelineItem>
                }

                {limitTriageItems && _.map(_.take(triageItems, maxTriageItems / 2), item =>
                  <TimelineItem key={`${item.id}-${item.type}`} user={item.claimedBy} date={item.createdAt}>
                    <div className="flex">
                      <TriageItem item={item} eagerUpdate={updateTriage} hideTicket />
                      <RemoveButton onClick={() => this.removeTriage(item)} title="Remove from ticket" />
                    </div>
                  </TimelineItem>
                )}

                {limitTriageItems &&
                  <TimelineItem>
                    <h4>{triageItems.length - maxTriageItems} more triage items hidden</h4>
                    <PrimaryButton onClick={() => this.setState({ showAllTriageItems: true })}>Show All</PrimaryButton>
                  </TimelineItem>
                }

                {limitTriageItems && _.map(_.takeRight(triageItems, maxTriageItems / 2), item =>
                  <TimelineItem key={`${item.id}-${item.type}`} user={item.claimedBy} date={item.createdAt}>
                    <div className="flex">
                      <TriageItem item={item} eagerUpdate={updateTriage} hideTicket />
                      <RemoveButton onClick={() => this.removeTriage(item)} title="Remove from ticket" />
                    </div>
                  </TimelineItem>
                )}

                {!limitTriageItems && _.map(triageItems, item =>
                  <TimelineItem key={`${item.id}-${item.type}`} user={item.claimedBy} date={item.createdAt}>
                    <div className="flex">
                      <TriageItem item={item} eagerUpdate={updateTriage} hideTicket />
                      <RemoveButton onClick={() => this.removeTriage(item)} title="Remove from ticket" />
                    </div>
                  </TimelineItem>
                )}

                <TimelineItem user={ticket.createdBy} date={ticket.createdAt}>
                  <h3>Ticket Opened</h3>
                </TimelineItem>
              </Timeline>
            </Card>
          </div>

          <div className="tasks">
            {!isArchived && <AddTask ticket={ticket} updateTicket={updateTicket} saveTask={saveNewTask} cancelAddTask={_.noop} />}
            <ul className="unstyled-list">
              <FlipMove>
                {_.map(tasks, (task) =>
                  <div key={task.uuid}>
                    <TaskComponent
                      task={task}
                      updateTask={(updates, ticketUpdates) => updateTask({ ..._.pick(task, ['uuid', 'ticket']), ...updates }, ticketUpdates)}
                      updateTicket={updateTicket}
                      ticket={ticket}
                      inTicket
                    />
                  </div>
                )}
              </FlipMove>
            </ul>
          </div>
        </div>
      </PageContent>
    );
  }
}

// Exports
export default connect((state) => ({
  ticket: state.tickets.ticket,
  triageItems: state.tickets.triageItems,
  currentUser: state.auth.currentUser,
  teams: state.pageData.teams
}))(TicketSummary);



// WEBPACK FOOTER //
// ./app/routes/ticket/sub-routes/ticket-summary.js